import React, { useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiUser, FiLogOut, FiHome } from 'react-icons/fi';
import { PrimaryButton, SecondaryButton } from '../styles/AdminComponents';

// Navbar-specific styled components
const NavContainer = styled.nav`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 50;
`;

const NavContent = styled.div`
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 4rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3b82f6;
  }
  
  .icon {
    font-size: 1.75rem;
    color: #3b82f6;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  background: ${props => props.active ? '#eff6ff' : 'transparent'};
  border: 1px solid ${props => props.active ? '#bfdbfe' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    color: #3b82f6;
    background: #f8fafc;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Username = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
`;

const RankBadge = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
  color: ${props => props.rank === 'owner' ? '#1e40af' : '#059669'};
  background: ${props => props.rank === 'owner' ? '#dbeafe' : '#dcfce7'};
  padding: 0.125rem 0.5rem;
  border-radius: 6px;
  margin-top: 0.125rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  @media (max-width: 1024px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 1rem;
  
  @media (min-width: 1025px) {
    display: none;
  }
`;

const MobileNavGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MobileNavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${props => props.active ? '#3b82f6' : '#374151'};
  background: ${props => props.active ? '#eff6ff' : '#f9fafb'};
  border: 1px solid ${props => props.active ? '#bfdbfe' : '#e5e7eb'};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  
  .icon {
    font-size: 1.125rem;
    color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fee2e2;
    transform: translateY(-1px);
  }
`;

function AdminNavbar({ onLogout, adminRank }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const username = localStorage.getItem('adminUsername') || 'Admin';

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: FiHome, exact: true },
    { path: '/admin/users', label: 'Users', icon: FiUser },
    ...(adminRank === 'owner' ? [
      { path: '/admin/admins', label: 'Admins', icon: FiUser },
      { path: '/admin/payments', label: 'Payments', icon: FiUser },
      { path: '/admin/referrers', label: 'Referrers', icon: FiUser },
      { path: '/admin/payouts', label: 'Payouts', icon: FiUser },
      { path: '/admin/camera', label: 'Camera', icon: FiUser }
    ] : []),
    { path: '/admin/feedback', label: 'Feedback', icon: FiUser }
  ];

  return (
    <NavContainer>
      <NavContent>
        <Logo to="/admin" onClick={closeMobileMenu}>
          <FiHome className="icon" />
          Admin Panel
        </Logo>

        <DesktopNav>
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              active={item.exact ? location.pathname === item.path : isActive(item.path)}
            >
              <item.icon />
              {item.label}
            </NavItem>
          ))}
        </DesktopNav>

        <UserSection>
          <UserInfo>
            <UserAvatar>
              {username.charAt(0).toUpperCase()}
            </UserAvatar>
            <UserDetails>
              <Username>{username}</Username>
              <RankBadge rank={adminRank}>{adminRank}</RankBadge>
            </UserDetails>
          </UserInfo>

          <SecondaryButton 
            onClick={onLogout}
            style={{ 
              padding: '0.5rem',
              minHeight: '40px',
              display: 'none'
            }}
            className="desktop-only"
          >
            <FiLogOut />
            <span style={{ display: 'none' }}>Logout</span>
          </SecondaryButton>

          <MobileMenuButton onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </MobileMenuButton>
        </UserSection>
      </NavContent>

      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileNavGrid>
          {navItems.map((item) => (
            <MobileNavItem
              key={item.path}
              to={item.path}
              active={item.exact ? location.pathname === item.path : isActive(item.path)}
              onClick={closeMobileMenu}
            >
              <item.icon className="icon" />
              {item.label}
            </MobileNavItem>
          ))}
        </MobileNavGrid>
        
        <LogoutButton onClick={() => { onLogout(); closeMobileMenu(); }}>
          <FiLogOut />
          Logout
        </LogoutButton>
      </MobileMenu>

      <style jsx>{`
        .desktop-only {
          display: flex !important;
        }
        
        @media (max-width: 768px) {
          .desktop-only {
            display: none !important;
          }
        }
      `}</style>
    </NavContainer>
  );
}

export default AdminNavbar;
